import { LinkOutlined } from '@ant-design/icons'
import { Button, Table } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { t } from 'i18next'
import Moment from 'react-moment'
import styled from 'styled-components'
import { dateFormat } from '../constants/constants'
import InclusionSwitch from './InclusionSwitch'
import { SearchTableRow } from './SearchTable'
import TableBoldColumnContainer from './display/TableBoldColumnContainer'

const FirstLineContainer = styled.div`
  margin: 0 8px 4px 8px;
`

const SecondLineContainer = styled.div`
  font-size: 12px;
  margin: 0 8px 0 8px;
`

const LanguageContainer = styled.div`
  text-transform: capitalize;
`

interface LiteratureReviewSearchTableProps {
  formattedData: SearchTableRow[]
  loadingIds?: string[]
  onClick?: (recordId: string) => void
  maxHeight?: string
  onSummarize?: (recordId: string) => void
  onSwitchClick?: (recordId: string) => void
}

const LiteratureReviewSearchTable = ({
  formattedData,
  loadingIds = [],
  onClick,
  maxHeight,
  onSummarize,
  onSwitchClick,
}: LiteratureReviewSearchTableProps) => {
  const renderActionButtons = (record: SearchTableRow) => {
    return (
      <div data-clickable>
        <Button
          type="link"
          color="default"
          icon={<LinkOutlined />}
          href={record.documentLink}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    )
  }

  const columns: ColumnsType<SearchTableRow> = [
    {
      title: t('Title'),
      dataIndex: 'title',
      key: 'title',
      width: '30%',
      render: (_: string, record: SearchTableRow) => (
        <TableBoldColumnContainer>{record.title}</TableBoldColumnContainer>
      ),
    },
    {
      title: t('Authors'),
      dataIndex: 'authors',
      key: 'authors',
      width: '30%',
      render: (_: string, record: SearchTableRow) => (
        <>
          <FirstLineContainer>
            {record.publicationName || t('searchTable.noJournalAvailable')}
          </FirstLineContainer>
          <SecondLineContainer>
            {record.authors?.join(', ') || t('searchTable.noAuthorAvailable')}
          </SecondLineContainer>
        </>
      ),
    },
    {
      title: t('Date'),
      dataIndex: 'publicationDate',
      key: 'publicationDate',
      width: '20%',
      render: (_: string, record: SearchTableRow) => (
        <>
          <FirstLineContainer>
            {record.publicationDate ? (
              <Moment
                date={record.publicationDate}
                format={dateFormat.PRIMARY}
              />
            ) : (
              t('searchTable.noDateAvailable')
            )}
          </FirstLineContainer>
          <SecondLineContainer>
            <LanguageContainer>
              {record.language || t('searchTable.noLanguageAvailable')}
            </LanguageContainer>
          </SecondLineContainer>
        </>
      ),
    },
    {
      title: (
        <>
          {t('searchTable.include')}/<br />
          {t('searchTable.exclude')}
        </>
      ),
      dataIndex: 'includeExclude',
      key: 'includeExclude',
      width: '15%',
      render: (_: string, record: SearchTableRow) => (
        <InclusionSwitch
          id={record.id}
          data-testid={`simple-switch-${record.id}`}
          checked={record.included}
          onClick={() => {
            onSwitchClick?.(record.id)
          }}
          loading={loadingIds?.includes(record.id)}
          disabled={loadingIds && loadingIds?.length > 0}
        />
      ),
    },
    {
      title: '',
      key: 'action',
      dataIndex: 'action',
      align: 'right',
      width: '5%',
      render: (_: string, record: SearchTableRow) =>
        renderActionButtons(record),
    },
  ]

  const handleRowClick = (e: React.MouseEvent, record: SearchTableRow) => {
    const target = e.target as HTMLElement
    const isClickable = target.closest('[data-clickable]')

    if (!isClickable) {
      onClick?.(record.id)
    }
  }

  return (
    <Table
      rowClassName="page-list-table-row clickable"
      columns={columns}
      dataSource={formattedData}
      scroll={{ y: maxHeight }}
      onRow={(record, rowIndex) => ({
        'data-testid': `literatureReviewTableRow${rowIndex}`,
        onClick: (event: React.MouseEvent) => {
          handleRowClick(event, record)
        },
      })}
    />
  )
}

export default LiteratureReviewSearchTable
